import { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Activity, ChevronLeft, ChevronRight, Users, FolderKanban, Database, MessageSquare, AlertTriangle, Settings, UserCheck } from 'lucide-react';

const ICONS = { auth: UserCheck, project: FolderKanban, dataset: Database, chat: MessageSquare, error: AlertTriangle, admin: Settings, feedback: MessageSquare };
const COLORS = { auth: 'bg-blue-500/10 text-blue-400', project: 'bg-emerald-500/10 text-emerald-400', dataset: 'bg-purple-500/10 text-purple-400', chat: 'bg-cyan-500/10 text-cyan-400', error: 'bg-red-500/10 text-red-400', admin: 'bg-amber-500/10 text-amber-400', feedback: 'bg-pink-500/10 text-pink-400' };

export default function AdminActivityPage() {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('all');
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, skip: page * limit, days: 90 };
      if (filter !== 'all') params.event_type = filter;
      const res = await adminAPI.activity(params);
      setEvents(res.data.events || []); setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 sm:p-6 max-w-[1400px]" data-testid="admin-activity">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Activity Log</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">All platform events</p>
        </div>
        <Select value={filter} onValueChange={(v) => { setFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="auth">Auth</SelectItem>
            <SelectItem value="project">Projects</SelectItem>
            <SelectItem value="chat">Chat</SelectItem>
            <SelectItem value="error">Errors</SelectItem>
            <SelectItem value="feedback">Feedback</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">{[...Array(10)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : events.length === 0 ? (
            <div className="text-center py-12"><Activity className="w-8 h-8 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No events found.</p></div>
          ) : (
            <div className="divide-y divide-border">
              {events.map((evt, i) => {
                const Icon = ICONS[evt.event_type] || Activity;
                const color = COLORS[evt.event_type] || 'bg-muted text-muted-foreground';
                return (
                  <div key={i} className="flex items-start sm:items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-muted/30 transition-colors">
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
                      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                        <span className="text-xs sm:text-sm font-medium">{evt.user_name || evt.user_email?.split('@')[0] || 'System'}</span>
                        <Badge variant="outline" className="text-[9px] sm:text-[10px] h-4 sm:h-5">{evt.event_name?.replace(/_/g, ' ')}</Badge>
                      </div>
                      {evt.metadata && Object.keys(evt.metadata).length > 0 && (
                        <p className="text-[10px] sm:text-[11px] text-muted-foreground mt-0.5 truncate">
                          {evt.metadata.message_preview || evt.metadata.name || evt.metadata.agent || JSON.stringify(evt.metadata).slice(0, 80)}
                        </p>
                      )}
                    </div>
                    <span className="text-[9px] sm:text-[11px] text-muted-foreground whitespace-nowrap flex-shrink-0">{new Date(evt.timestamp).toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {total > limit && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-[10px] sm:text-xs text-muted-foreground">{page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 0} className="h-7 w-7 p-0"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total} className="h-7 w-7 p-0"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
